import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCompanyInfo } from '../../context/CompanyInfoContext';
import { useLoading } from '../../context/LoadingContext';
import { useRoutePath } from '../../utils/getRoutePath';
import Breadcrumb from '../common/Breadcrumb';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import {
  BuildingOffice2Icon,
  MapPinIcon,
  PhoneIcon,
  UserIcon,
  ArrowLeftIcon,
  PencilIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';

const CompanyInfoView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { companyInfo, getCompanyInfo, getCompanyInfoById } = useCompanyInfo();
  const { hideLoading } = useLoading();
  const getRoutePath = useRoutePath();
  const { countries } = useCountry();

  const [company, setCompany] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const hasFetched = useRef(false);

  const fetchCompany = useCallback(
    async () => {
      if (!id || hasFetched.current) return;
      hasFetched.current = true;

      setIsFetching(true);
      //showLoading();

      try {
        // Try to get from already loaded context first
        let found = getCompanyInfoById(id);

        // If not found (e.g. direct URL hit), load all companies once
        if (!found) {
          const response = await getCompanyInfo(1, { all: true });

          if (response) {
            const list = Array.isArray(response?.data) ? response.data : response.data?.data;
            if (Array.isArray(list)) {
              found = list.find(item => Number(item.id) === Number(id));
            }
          }
        }

        if (found) {
          setCompany(found);
        } else {
          setCompany(null);
        }
      } catch (error) {
        console.error('Error fetching company info:', error);
        setCompany(null);
      } finally {
        setIsFetching(false);
        hideLoading();
      }
    },
    [id, getCompanyInfoById, getCompanyInfo, hideLoading]
  );

  useEffect(() => {
    // If list is already in context we might be able to resolve quickly
    if (id && companyInfo?.data?.length && !company) {
      const existing = companyInfo.data.find(item => Number(item.id) === Number(id));
      if (existing) {
        setCompany(existing);
        setIsFetching(false);
        return;
      }
    }

    fetchCompany();
  }, [id, companyInfo, company, fetchCompany]);

  const getCountryName = () => {
    if (!company?.country_id || !Array.isArray(countries)) return 'N/A';
    const match = countries.find(
      (country) => Number(country.id) === Number(company.country_id)
    );
    return match?.name || 'N/A';
  };

  const getStatusBadge = () => {
    const isActive = company?.active;

    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
          isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}
      >
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  const canEdit = user && (user.role_id === 1 || user.role_id === 2);

  if (!isFetching && !company) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Company Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            The company you&apos;re looking for doesn&apos;t exist or has been removed.
          </p>
          <button
            onClick={() => navigate(getRoutePath('/company-info'))}
            className="inline-flex items-center px-4 py-2 bg-[#009efb] text-white rounded-lg hover:bg-[#0089db] transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Companies
          </button>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-3">
          <div className="h-8 w-8 border-2 border-[#009efb] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-600">Loading company information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <Breadcrumb />
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mt-4 space-y-4 md:space-y-0">
            {/* Left side */}
            <div className="flex items-start md:items-center space-x-3">
              <div className="p-2 bg-[#009efb] rounded-lg">
                <BuildingOffice2Icon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center flex-wrap gap-2">
                  <span>{company.name || 'Company Details'}</span>
                  {company.alias && (
                    <span className="text-sm font-medium text-gray-500">
                      ({company.alias})
                    </span>
                  )}
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-600">
                  {company.id && (
                    <span>Company ID #{company.id}</span>
                  )}
                  <span>{getStatusBadge()}</span>
                </div>
              </div>
            </div>

            {/* Right side buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:flex-wrap gap-2 w-full md:w-auto">
              <button
                onClick={() => navigate(getRoutePath('/company-info'))}
                className="inline-flex items-center justify-center px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md bg-white hover:bg-gray-50 transition-colors w-full sm:w-auto"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to List
              </button>

              {canEdit && (
                <button
                  onClick={() => navigate(getRoutePath(`/company-info/edit/${company.id}`))}
                  className="inline-flex items-center justify-center px-4 py-2 text-sm bg-[#009efb] text-white rounded-md hover:bg-[#0089db] transition-colors w-full sm:w-auto"
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Edit Company
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="xl:col-span-2 space-y-6">
            {/* Company Overview Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <BuildingOffice2Icon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">
                    Company Overview
                  </h2>
                </div>
              </div>
              <div className="p-6 space-y-6">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                  <div className="flex-shrink-0">
                    {company.logo?.path ? ( 
                      <img
                        src={company.logo.path}
                        alt="Company Logo"
                        className="h-20 w-20 rounded-lg object-contain bg-gray-50 border border-gray-200"
                      />
                    ) : (
                      <div className="h-20 w-20 rounded-lg bg-gray-50 border border-dashed border-gray-300 flex items-center justify-center">
                        <PhotoIcon className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Company Name
                      </p>
                      <p className="text-lg font-semibold text-gray-900">
                        {company.name || 'N/A'}
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Company Alias
                        </p>
                        <p className="text-sm text-gray-900">
                          {company.alias || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          EIN / Business Number / License
                        </p>
                        <p className="text-sm text-gray-900">
                          {company.business_number || 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Company Slogan
                      </p>
                      <p className="text-sm text-gray-900 bg-gray-50 rounded-lg p-3">
                        {company.slogan || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <PhoneIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">
                    Contact Information
                  </h2>
                </div>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Primary Phone
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {company.primary_number || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Secondary Phone
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {company.secondary_number || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Primary Email
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900 break-all">
                      {company.primary_email || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Secondary Email
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900 break-all">
                      {company.secondary_email || 'N/A'}
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <UserIcon className="h-5 w-5 text-gray-500 mr-2" />
                    Contacts
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        Primary Contact
                      </p>
                      <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                        {`${company.primary_contact_first_name || ''} ${
                          company.primary_contact_last_name || ''
                        }`.trim() || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        Secondary Contact
                      </p>
                      <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                        {`${company.secondary_contact_first_name || ''} ${
                          company.secondary_contact_last_name || ''
                        }`.trim() || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Address & Summary */}
          <div className="space-y-6">
            {/* Address Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center">
                  <MapPinIcon className="h-5 w-5 text-gray-600 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    Address
                  </h2>
                </div>
              </div>
              <div className="p-6 space-y-4 text-sm">
                <div>
                  <span className="text-gray-600 block mb-1">Street Address 1</span>
                  <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                    {company.address1 || 'N/A'}
                  </div>
                </div>
                <div>
                  <span className="text-gray-600 block mb-1">Street Address 2</span>
                  <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                    {company.address2 || 'N/A'}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-gray-600 block mb-1">City</span>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {company.city || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600 block mb-1">State / Province</span>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {company.state || 'N/A'}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-gray-600 block mb-1">Zip / Postal Code</span>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {company.zipcode || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600 block mb-1">Country</span>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {getCountryName()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Summary Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">
                  Company Summary
                </h2>
              </div>
              <div className="p-6 space-y-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status</span>
                  {getStatusBadge()}
                </div>

                {company.formated_created_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Created At</span>
                    <span className="font-medium text-gray-900">
                      {company.formated_created_at}
                    </span>
                  </div>
                )}

                {company.formated_updated_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Last Updated</span>
                    <span className="font-medium text-gray-900">
                      {company.formated_updated_at}
                    </span>
                  </div>
                )}

                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    Manage this company&apos;s information from the{' '}
                    <Link
                      to={getRoutePath('/company-info')}
                      className="text-[#009efb] hover:text-[#0089db] font-medium"
                    >
                      Company List
                    </Link>{' '}
                    or edit directly using the button above.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyInfoView;